import { invert } from 'lodash-es';
import { DenovoLogoUrl, EnhanceLogoUrl, KidneyTrialLogoUrl } from '@icons';
import { CustomCustomerCaseStatus } from './case-status.constants';
import {
  AberrantAnatomyType,
  AssistanceType,
  BacktableInjuryType,
  CaseLogEventType,
  CaseLogGroup,
  CaseResourceType,
  CaseSalesOrderStatus,
  CaseStatus,
  CaseUserGroup,
  CustomerCaseStatus,
  DispositionReasonType,
  DispositionSpecificationType,
  FlushType,
  ImpactOfInjuryType,
  InvoiceState,
  InvoiceStatus,
  Laterality,
  LiverOutcome,
  LiverQuality,
  NoLiverInjuryType,
  NRPType,
  OrganType,
  OutcomeType,
  ProcurementEnjuryType,
  QuoteRejectReason,
  QuoteStatus,
  RandomizationFilterType,
  RandomizationResultType,
  ResourceLogisticsStatus,
  ReviewStatus,
  RoutePlanStatus,
  ServiceType,
  TrialAnswer,
  TurndownOutcome,
} from '@/api/apis/case';
import { EmployeeRole } from '@/api/apis/resources';
import { RouteLegTypeEnum, TransportProviderTypes } from '@/api/apis/route';
import { UserRole } from '@/api/apis/user';
import { GroupWeightType, OrganAcceptanceCriteriaWeightType, RoleKeyedPartial, RolesWeightType } from '@/types';
import { CustomerRole, CustomerRoleT } from '@/api/apis/customer';
import { clinicalManagementGroups, logisticsManagementGroups } from '@/constants/user.constants';

export const organSuffixes = {
  [OrganType.Heart]: 'HT',
  [OrganType.Liver]: 'LI',
  [OrganType.Lungs]: 'LU',
  [OrganType.Kidney]: 'KI',
} as const;

export const KIDNEY_LATERALITY_LETTER: Record<Laterality.Left | Laterality.Right, string> = {
  [Laterality.Left]: 'L',
  [Laterality.Right]: 'R',
};

export const ORGAN_SELECT_LIST = Object.values(OrganType).map((o) => ({ name: o, value: o }));

export const SCHEDULE_ORGAN_SELECT_LIST = ORGAN_SELECT_LIST;

// Remove with translations
export const caseStatusNames: Record<CaseStatus, string> = {
  [CaseStatus.None]: 'None',
  [CaseStatus.Draft]: 'Draft',
  [CaseStatus.OpoDraft]: 'OPO Draft',
  [CaseStatus.TXCenterDraft]: 'Tx Center Draft',
  [CaseStatus.ClinicalScreening]: 'Clinical Screening',
  [CaseStatus.Accepted]: 'Case Accepted',
  [CaseStatus.Declined]: 'Case Declined',
  [CaseStatus.PendingLogistics]: 'Pending Logistics',
  [CaseStatus.ResourcesBooked]: 'Resources Assigned',
  [CaseStatus.ResourceDeployed]: 'Resource(s) Deployed',
  [CaseStatus.AtDonorHospital]: 'At Donor Hospital',
  [CaseStatus.OrganOnOCS]: 'On OCS',
  [CaseStatus.DeployedToTXCenter]: 'In Transit to TX Center',
  [CaseStatus.AtTXCenter]: 'At TX Center',
  [CaseStatus.OrganOffOCS]: 'Off OCS',
  [CaseStatus.ServiceDeclined]: 'Transplant not Completed',
  [CaseStatus.ServiceCompleted]: 'NOP Service Completed',
  [CaseStatus.ReturningToBase]: 'Returning to Base',
  [CaseStatus.Finished]: 'Case Finished',
  [CaseStatus.Closed]: 'Case Closed',
  [CaseStatus.New]: 'New',
  [CaseStatus.OrTimeConfirmed]: 'OR Time Confirmed',
  [CaseStatus.QuoteReceived]: 'Quote Received',
  [CaseStatus.QuoteSent]: 'Quote Sent',
  [CaseStatus.QuoteRejected]: 'Quote Rejected',
  [CaseStatus.QuoteApproved]: 'Quote Approved',
  [CaseStatus.Deleted]: 'Deleted',
};

export const caseOutcomeNames: Record<CaseStatus, string> = caseStatusNames;

export const quoteStatusNames: Record<QuoteStatus, string> = {
  [QuoteStatus.Rejected]: 'Quote Rejected',
  [QuoteStatus.NoQuote]: 'No Quote',
  [QuoteStatus.Created]: 'Quote Created',
  [QuoteStatus.ApprovedInternally]: 'Approved Internally',
  [QuoteStatus.RejectedInternally]: 'Rejected Internally',
  [QuoteStatus.SentToTXCenter]: 'Sent To TX Review',
  [QuoteStatus.InvoiceSent]: 'Invoice Sent',
  [QuoteStatus.InvoicePaid]: 'Invoice Paid',
  [QuoteStatus.Approved]: 'Quote Approved',
  [QuoteStatus.Recalled]: 'Quote Recalled',
};

export const routeStatusNames: Record<RoutePlanStatus, string> = {
  [RoutePlanStatus.RoutePlanningStart]: 'Route Planning',
  [RoutePlanStatus.WaitingForAviationApproval]: 'Waiting For Aviation Approval',
  [RoutePlanStatus.WaitingForQuoteApproval]: 'Quote Sent for Top 3 Approval',
  [RoutePlanStatus.RoutePlanningDone]: 'Route Planning Done',
  [RoutePlanStatus.PendingResourcesBooking]: 'Acknowledgement Pending',
  [RoutePlanStatus.ResourcesBooked]: 'Resources Assigned',
};

export const boardStatusList = [
  CaseStatus.Draft,
  CaseStatus.ClinicalScreening,
  CaseStatus.Accepted,
  CaseStatus.Declined,
  CaseStatus.PendingLogistics,
  CaseStatus.ResourcesBooked,
  CaseStatus.ResourceDeployed,
  CaseStatus.AtDonorHospital,
  CaseStatus.OrganOnOCS,
  CaseStatus.DeployedToTXCenter,
  CaseStatus.AtTXCenter,
  CaseStatus.OrganOffOCS,
  CaseStatus.ServiceDeclined,
  CaseStatus.ReturningToBase,
];

export const activeCaseStatuses: CaseStatus[] = [
  CaseStatus.ClinicalScreening,
  CaseStatus.Accepted,
  CaseStatus.PendingLogistics,
  CaseStatus.ResourcesBooked,
  CaseStatus.ResourceDeployed,
  CaseStatus.AtDonorHospital,
  CaseStatus.OrganOnOCS,
  CaseStatus.AtTXCenter,
  CaseStatus.OrganOffOCS,
  CaseStatus.ReturningToBase,
  CaseStatus.DeployedToTXCenter,
  CaseStatus.ServiceDeclined,
];

export const statusList = [
  CaseStatus.Draft,
  CaseStatus.ClinicalScreening,
  CaseStatus.Accepted,
  CaseStatus.Declined,
  CaseStatus.PendingLogistics,
  CaseStatus.ResourcesBooked,
  CaseStatus.ResourceDeployed,
  CaseStatus.AtDonorHospital,
  CaseStatus.OrganOnOCS,
  CaseStatus.DeployedToTXCenter,
  CaseStatus.AtTXCenter,
  CaseStatus.OrganOffOCS,
  CaseStatus.ServiceDeclined,
  CaseStatus.ReturningToBase,
  CaseStatus.Finished,
  CaseStatus.Closed,
];

export const nextCaseStatus = {
  [CaseStatus.ClinicalScreening]: CaseStatus.Accepted,
  [CaseStatus.Accepted]: CaseStatus.PendingLogistics,
  [CaseStatus.PendingLogistics]: CaseStatus.ResourcesBooked,
  [CaseStatus.ResourcesBooked]: CaseStatus.ResourceDeployed,
  [CaseStatus.ResourceDeployed]: CaseStatus.AtDonorHospital,
  [CaseStatus.AtDonorHospital]: CaseStatus.OrganOnOCS,
  [CaseStatus.OrganOnOCS]: CaseStatus.DeployedToTXCenter,
  [CaseStatus.DeployedToTXCenter]: CaseStatus.AtTXCenter,
  [CaseStatus.AtTXCenter]: CaseStatus.OrganOffOCS,
  [CaseStatus.OrganOffOCS]: CaseStatus.ReturningToBase,
  [CaseStatus.ReturningToBase]: CaseStatus.Finished,
  [CaseStatus.Finished]: CaseStatus.Closed,
  [CaseStatus.Declined]: CaseStatus.Closed,
  [CaseStatus.ServiceDeclined]: CaseStatus.Finished,
} as const;

export const prevCaseStatus = {
  [CaseStatus.PendingLogistics]: CaseStatus.Accepted,
  [CaseStatus.ResourcesBooked]: CaseStatus.PendingLogistics,
  [CaseStatus.ResourceDeployed]: CaseStatus.ResourcesBooked,
  [CaseStatus.AtDonorHospital]: CaseStatus.ResourceDeployed,
  [CaseStatus.OrganOnOCS]: CaseStatus.AtDonorHospital,
  [CaseStatus.DeployedToTXCenter]: CaseStatus.OrganOnOCS,
  [CaseStatus.AtTXCenter]: CaseStatus.DeployedToTXCenter,
  [CaseStatus.OrganOffOCS]: CaseStatus.AtTXCenter,
  [CaseStatus.ReturningToBase]: CaseStatus.OrganOffOCS,
  [CaseStatus.Finished]: CaseStatus.ReturningToBase,
};

export const statusesForServiceDecline = [
  CaseStatus.Accepted,
  CaseStatus.PendingLogistics,
  CaseStatus.ResourcesBooked,
  CaseStatus.ResourceDeployed,
  CaseStatus.AtDonorHospital,
  CaseStatus.OrganOnOCS,
  CaseStatus.DeployedToTXCenter,
  CaseStatus.AtTXCenter,
];

export const logisticsStatusList: ResourceLogisticsStatus[] = [
  ResourceLogisticsStatus.None,
  ResourceLogisticsStatus.Available,
  ResourceLogisticsStatus.Reserved,
  ResourceLogisticsStatus.BookingSent,
  ResourceLogisticsStatus.Booked,
  ResourceLogisticsStatus.BookedInMao,
  ResourceLogisticsStatus.DeployedToDonorHospital,
  ResourceLogisticsStatus.AtDonorHospital,
  ResourceLogisticsStatus.DeployedFromDonorHospital,
  ResourceLogisticsStatus.DeployedToTX,
  ResourceLogisticsStatus.AtTXCenter,
  ResourceLogisticsStatus.DeployedFromTXCenter,
  ResourceLogisticsStatus.DeployedToHub,
  ResourceLogisticsStatus.ReturningToBase,
  ResourceLogisticsStatus.OCSOnCase,
  ResourceLogisticsStatus.OCSDeployed,
  ResourceLogisticsStatus.OCSDone,
  ResourceLogisticsStatus.Done,
  ResourceLogisticsStatus.OnRest,
  ResourceLogisticsStatus.RecoveryIsCompleted,
  ResourceLogisticsStatus.OnCase,
  ResourceLogisticsStatus.CancelledInMao,
];

export const caseServiceTypes = {
  [ServiceType.NopWithTmdxSurgeon]: 'NOP with TMDX Surgeon',
  [ServiceType.NopWithoutTmdxSurgeon]: 'NOP without TMDX Surgeon',
  [ServiceType.DirectAcquisition]: 'Direct Acquisition',
};

export const NON_CUSTOMER_SERVICE_TYPES: ServiceType[] = [ServiceType.DirectAcquisition];

export const routeLegToProviderTypeMap: Record<RouteLegTypeEnum, TransportProviderTypes> = {
  [RouteLegTypeEnum.TmdxJetAviaLeg]: TransportProviderTypes.TMDX,
  [RouteLegTypeEnum.SupplementalLiftAviaLeg]: TransportProviderTypes.SupplementalLiftProvider,
  [RouteLegTypeEnum.TxCenterProviderAviaLeg]: TransportProviderTypes.TxCenter,
  [RouteLegTypeEnum.CommercialFlightAviaLeg]: TransportProviderTypes.Commercial,
  [RouteLegTypeEnum.TmdxAffiliateGroundLeg]: TransportProviderTypes.TMDXAffiliate,
  [RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg]: TransportProviderTypes.TMDXAffiliate,
  [RouteLegTypeEnum.TxCenterProviderGroundLeg]: TransportProviderTypes.TxCenter,
  [RouteLegTypeEnum.CurriServiceGroundLeg]: TransportProviderTypes.Curri,
  [RouteLegTypeEnum.TmdxDriveTedsGroundLeg]: TransportProviderTypes.Teds,
  [RouteLegTypeEnum.AccommodationLeg]: TransportProviderTypes.Partner,
  [RouteLegTypeEnum.SelfServicedGroundLeg]: TransportProviderTypes.SelfServiced,
};

export const caseLogisticsProviders = {
  [TransportProviderTypes.TMDX]: 'TMDX',
  [TransportProviderTypes.Commercial]: 'Commercial',
  [TransportProviderTypes.Partner]: 'Partner',
  [TransportProviderTypes.SupplementalLiftProvider]: 'Supplemental Lift Provider',
  [TransportProviderTypes.TMDXAffiliate]: 'TMDX Affiliate',
  [TransportProviderTypes.TxCenter]: 'TX Center provider',
  [TransportProviderTypes.Curri]: 'Curri',
  [TransportProviderTypes.Teds]: 'TEDS',
  [TransportProviderTypes.SelfServiced]: 'Self-Serviced',
};

export const turndownOutcomeNames: Record<TurndownOutcome, string> = {
  [TurndownOutcome.DiscardOrgan]: 'Discard Organ',
  [TurndownOutcome.ShipOrgan]: 'Ship Organ',
  [TurndownOutcome.RellocateOrgan]: 'Reallocate organ',
};

export const nrpTypes: Record<NRPType, string> = {
  [NRPType.none]: 'None',
  [NRPType.aNrp]: 'A-NRP',
  [NRPType.taNrp]: 'TA-NRP',
};

export const caseResourceTypeNames: Record<CaseResourceType, string> = {
  [CaseResourceType.Employee]: 'Employee',
  [CaseResourceType.OCSConsole]: 'OCS Console',
  [CaseResourceType.Aircraft]: 'Aircraft',
  [CaseResourceType.ClinicalSpecialist]: 'Clinical Specialist',
  [CaseResourceType.Surgeon]: 'Surgeon',
  [CaseResourceType.Crew]: 'TMDX Pilot',
};

export const caseLogEventToGroupMap: Record<CaseLogEventType, CaseLogGroup> = {
  [CaseLogEventType.CaseDraftCreated]: CaseLogGroup.CaseManagement,
  [CaseLogEventType.CaseClinicalScreeningCreated]: CaseLogGroup.CaseManagement,
  [CaseLogEventType.CaseTxCenterDraftCreated]: CaseLogGroup.CaseManagement,
  [CaseLogEventType.CaseDetailsUpdated]: CaseLogGroup.CaseUpdate,
  [CaseLogEventType.CaseRecipientHospitalORDataUpdated]: CaseLogGroup.CaseUpdate,
  [CaseLogEventType.CaseCreated]: CaseLogGroup.CaseManagement,
  [CaseLogEventType.CaseDraftDeleted]: CaseLogGroup.CaseManagement,
  [CaseLogEventType.CaseStatusUpdated]: CaseLogGroup.CaseManagement,
  [CaseLogEventType.CaseRouteAdded]: CaseLogGroup.CaseLogistics,
  [CaseLogEventType.CaseResourceReserved]: CaseLogGroup.ResourceManagement,
  [CaseLogEventType.CaseResourceReleased]: CaseLogGroup.ResourceManagement,
  [CaseLogEventType.CaseResourceAssigned]: CaseLogGroup.ResourceManagement,
  [CaseLogEventType.CaseResourceReassigned]: CaseLogGroup.ResourceManagement,
  [CaseLogEventType.CaseResourceUnassigned]: CaseLogGroup.ResourceManagement,
  [CaseLogEventType.CaseResourceInvitationAccepted]: CaseLogGroup.ResourceManagement,
  [CaseLogEventType.CaseResourceInvitationDeclined]: CaseLogGroup.ResourceManagement,
  [CaseLogEventType.ResourceStatusUpdated]: CaseLogGroup.ResourceManagement,
  [CaseLogEventType.DonorORTimeDelayAdded]: CaseLogGroup.CaseUpdate,
  [CaseLogEventType.DonorORTimeDelayUpdated]: CaseLogGroup.CaseUpdate,
  [CaseLogEventType.CaseNoteAdded]: CaseLogGroup.CaseUpdate,
  [CaseLogEventType.CaseNoteRemoved]: CaseLogGroup.CaseUpdate,
  [CaseLogEventType.CaseNoteUpdated]: CaseLogGroup.CaseUpdate,
  [CaseLogEventType.CustomerFeedbackAdded]: CaseLogGroup.CaseUpdate,
  [CaseLogEventType.InvoiceCreated]: CaseLogGroup.FinancialManagement,
  [CaseLogEventType.AttachFiles]: CaseLogGroup.CaseUpdate,
  [CaseLogEventType.DetachFile]: CaseLogGroup.CaseUpdate,
  [CaseLogEventType.OrganAcceptanceCriteriaUpdated]: CaseLogGroup.CaseUpdate,
  [CaseLogEventType.OrganAcceptanceCriteriaCleared]: CaseLogGroup.CaseUpdate,
  [CaseLogEventType.OrganAcceptanceCriteriaReset]: CaseLogGroup.CaseUpdate,
  [CaseLogEventType.ReAllocatedCaseCreated]: CaseLogGroup.CaseManagement,
  [CaseLogEventType.ReAllocatedCaseUpdated]: CaseLogGroup.CaseManagement,
  [CaseLogEventType.ReAllocatedCaseRemoved]: CaseLogGroup.CaseManagement,
  [CaseLogEventType.CaseReviewUpdated]: CaseLogGroup.CaseLogistics,
  [CaseLogEventType.CaseQuoteCommentSet]: CaseLogGroup.FinancialManagement,
  [CaseLogEventType.CaseQuoteCreated]: CaseLogGroup.FinancialManagement,
  [CaseLogEventType.CaseQuoteUpdated]: CaseLogGroup.FinancialManagement,
  [CaseLogEventType.CaseQuoteRedacted]: CaseLogGroup.FinancialManagement,
  [CaseLogEventType.CaseQuoteRejectedInternally]: CaseLogGroup.FinancialManagement,
  [CaseLogEventType.CaseQuoteApprovedInternally]: CaseLogGroup.FinancialManagement,
  [CaseLogEventType.CaseQuoteSentToTxCenter]: CaseLogGroup.FinancialManagement,
  [CaseLogEventType.CaseQuoteRejectedByTxCenter]: CaseLogGroup.FinancialManagement,
  [CaseLogEventType.CaseQuoteApprovedByTxCenter]: CaseLogGroup.FinancialManagement,
  [CaseLogEventType.CaseQuoteApprovedTxCenterProvider]: CaseLogGroup.FinancialManagement,
  [CaseLogEventType.CustomRouteInitiated]: CaseLogGroup.CaseLogistics,
  [CaseLogEventType.RoutePlanningStatusUpdated]: CaseLogGroup.CaseManagement,
  [CaseLogEventType.CaseResourceLogisticsStatusUpdated]: CaseLogGroup.ResourceManagement,
  [CaseLogEventType.CaseRouteUpdated]: CaseLogGroup.CaseLogistics,
  [CaseLogEventType.CaseRouteInitiated]: CaseLogGroup.CaseLogistics,
  [CaseLogEventType.CaseDeclined]: CaseLogGroup.CaseManagement,
  [CaseLogEventType.ServiceDeclined]: CaseLogGroup.CaseManagement,
  [CaseLogEventType.OpoContactsAssigned]: CaseLogGroup.ResourceManagement,
  [CaseLogEventType.OpoContactsUnassigned]: CaseLogGroup.ResourceManagement,
  [CaseLogEventType.OpoContactIsPrimaryChanged]: CaseLogGroup.ResourceManagement,
  [CaseLogEventType.CaseResourceStartTimeEstablished]: CaseLogGroup.CaseManagement,
  [CaseLogEventType.CaseLinkAdded]: CaseLogGroup.CaseUpdate,
  [CaseLogEventType.CaseLinkUpdated]: CaseLogGroup.CaseUpdate,
  [CaseLogEventType.CaseLinkRemoved]: CaseLogGroup.CaseUpdate,
  [CaseLogEventType.RequestOrganApproval]: CaseLogGroup.CaseManagement,
  [CaseLogEventType.OrganApproved]: CaseLogGroup.CaseManagement,
  [CaseLogEventType.OrganDeclined]: CaseLogGroup.CaseManagement,
  [CaseLogEventType.CreateSurgeonCaseSummary]: CaseLogGroup.CaseManagement,
  [CaseLogEventType.CaseInvoiceCreated]: CaseLogGroup.FinancialManagement,
  [CaseLogEventType.LightsSirensSupportChanged]: CaseLogGroup.CaseLogistics,
  [CaseLogEventType.RouteCommentUpdated]: CaseLogGroup.CaseLogistics,
  [CaseLogEventType.ServiceCompleted]: CaseLogGroup.CaseManagement,
  [CaseLogEventType.CaseDataSyncStatusChanged]: CaseLogGroup.CaseManagement,
  [CaseLogEventType.TransplantOutcomeTypeSet]: CaseLogGroup.CaseManagement,
  [CaseLogEventType.CaseSalesOrderUpdated]: CaseLogGroup.FinancialManagement,
  [CaseLogEventType.CaseAssignedEmployeePrimaryChanged]: CaseLogGroup.ResourceManagement,
  [CaseLogEventType.CaseQuoteRecalled]: CaseLogGroup.FinancialManagement,
  [CaseLogEventType.RandomizationResultSet]: CaseLogGroup.CaseManagement,
  [CaseLogEventType.QuestionnaireResultSet]: CaseLogGroup.CaseUpdate,
  [CaseLogEventType.IdentityAccessTicketGranted]: CaseLogGroup.AchieveCaseAccess,
  [CaseLogEventType.IdentityAccessTicketRevoked]: CaseLogGroup.AchieveCaseAccess,
  [CaseLogEventType.ResourceTransferredToCase]: CaseLogGroup.CaseManagement,
  [CaseLogEventType.ReAllocationClinicalDataCopied]: CaseLogGroup.CaseManagement,
  [CaseLogEventType.NotesCopied]: CaseLogGroup.CaseManagement,
  [CaseLogEventType.QuestionaryEditAllowanceChanged]: CaseLogGroup.CaseManagement,
  [CaseLogEventType.KidneyTrialChanged]: CaseLogGroup.CaseUpdate,
  [CaseLogEventType.KidneyRandomizationResultSet]: CaseLogGroup.CaseManagement,
  [CaseLogEventType.KidneyRandomizationResultRemoved]: CaseLogGroup.CaseManagement,
  [CaseLogEventType.CaseQuoteDryRunDiscountExcluded]: CaseLogGroup.FinancialManagement,
  [CaseLogEventType.CaseResourceLabelsChanged]: CaseLogGroup.ResourceManagement,
  [CaseLogEventType.DonorDataUpdated]: CaseLogGroup.CaseManagement,
  [CaseLogEventType.DonorDataReset]: CaseLogGroup.CaseManagement,
  [CaseLogEventType.CaseOpoDraftCreated]: CaseLogGroup.CaseManagement,
  [CaseLogEventType.CaseSplitIntoOrgans]: CaseLogGroup.CaseManagement,
  [CaseLogEventType.CaseInitiatedFromAnotherDraft]: CaseLogGroup.CaseManagement,
};

export const caseLogEventNames: Record<CaseLogEventType, string> = {
  [CaseLogEventType.CaseDraftCreated]: 'Case draft created',
  [CaseLogEventType.CaseClinicalScreeningCreated]: 'Clinical screening case created',
  [CaseLogEventType.CaseTxCenterDraftCreated]: 'Case TX Center draft created',
  [CaseLogEventType.CaseOpoDraftCreated]: 'Case OPO draft created',
  [CaseLogEventType.CaseDetailsUpdated]: 'Case details updated',
  [CaseLogEventType.CaseRecipientHospitalORDataUpdated]: 'Case details updated',
  [CaseLogEventType.CaseCreated]: 'Case has been created',
  [CaseLogEventType.CaseDraftDeleted]: 'Case draft deleted',
  [CaseLogEventType.CaseStatusUpdated]: 'Case status updated',
  [CaseLogEventType.CaseRouteAdded]: 'Case route added',
  [CaseLogEventType.CaseResourceReserved]: 'Case resource reserved',
  [CaseLogEventType.CaseResourceReleased]: 'Case resource released',
  [CaseLogEventType.CaseResourceAssigned]: 'Case resource assigned ',
  [CaseLogEventType.CaseResourceReassigned]: 'Case resource reassigned',
  [CaseLogEventType.CaseResourceUnassigned]: 'Case resource unassigned ',
  [CaseLogEventType.CaseResourceInvitationAccepted]: 'Case resource invitation accepted',
  [CaseLogEventType.CaseResourceInvitationDeclined]: 'Case resource invitation declined',
  [CaseLogEventType.ResourceStatusUpdated]: 'Case resource status updated',
  [CaseLogEventType.DonorORTimeDelayAdded]: 'Donor OR time delay Added',
  [CaseLogEventType.DonorORTimeDelayUpdated]: 'Donor OR time delay Updated',
  [CaseLogEventType.CaseNoteAdded]: 'Case Note Added',
  [CaseLogEventType.CaseNoteRemoved]: 'Case Note Removed',
  [CaseLogEventType.CaseNoteUpdated]: 'Case Note Updated',
  [CaseLogEventType.CustomerFeedbackAdded]: 'Customer feedback added',
  [CaseLogEventType.InvoiceCreated]: 'Invoice created',
  [CaseLogEventType.AttachFiles]: 'File attached to case',
  [CaseLogEventType.DetachFile]: 'File detached from case',
  [CaseLogEventType.OrganAcceptanceCriteriaUpdated]: 'Update Organ Acceptance Criteria',
  [CaseLogEventType.OrganAcceptanceCriteriaCleared]: 'Organ Acceptance Criteria cleared',
  [CaseLogEventType.OrganAcceptanceCriteriaReset]: 'Organ Acceptance Criteria reset',
  [CaseLogEventType.ReAllocatedCaseCreated]: 'Created as Re-allocated from',
  [CaseLogEventType.ReAllocatedCaseUpdated]: 'Update as Re-allocated from',
  [CaseLogEventType.ReAllocatedCaseRemoved]: 'Re-allocated case detached',
  [CaseLogEventType.CaseReviewUpdated]: 'Case review updated',
  [CaseLogEventType.CaseQuoteCommentSet]: 'Case quote comment',
  [CaseLogEventType.CaseQuoteCreated]: 'Case quote created',
  [CaseLogEventType.CaseQuoteUpdated]: 'Case quote updated',
  [CaseLogEventType.CaseQuoteRedacted]: 'Case quote redacted',
  [CaseLogEventType.CaseQuoteRejectedInternally]: 'Case quote rejected internally',
  [CaseLogEventType.CaseQuoteApprovedInternally]: 'Case quote approved internally',
  [CaseLogEventType.CaseQuoteSentToTxCenter]: 'Case quote sent to TX Center',
  [CaseLogEventType.CaseQuoteRejectedByTxCenter]: 'Case quote rejected by TX Center',
  [CaseLogEventType.CaseQuoteApprovedByTxCenter]: 'Case quote approved by TX Center',
  [CaseLogEventType.CaseQuoteApprovedTxCenterProvider]: 'TX Center provider approved',
  [CaseLogEventType.CaseQuoteDryRunDiscountExcluded]: 'Dry Run Discount Excluded',
  [CaseLogEventType.CustomRouteInitiated]: 'Case custom route initiated',
  [CaseLogEventType.RoutePlanningStatusUpdated]: 'Route planning status update',
  [CaseLogEventType.CaseResourceLogisticsStatusUpdated]: 'Case resource logistics status update',
  [CaseLogEventType.CaseRouteUpdated]: 'Case route updated',
  [CaseLogEventType.CaseRouteInitiated]: 'Case route initiated',
  [CaseLogEventType.CaseDeclined]: 'Case declined',
  [CaseLogEventType.ServiceDeclined]: 'Service declined',
  [CaseLogEventType.OpoContactsAssigned]: 'Opo contacts assigned',
  [CaseLogEventType.OpoContactsUnassigned]: 'Opo contacts unassigned',
  [CaseLogEventType.OpoContactIsPrimaryChanged]: 'Primary opo contact changed',
  [CaseLogEventType.CaseResourceStartTimeEstablished]: 'Case resource start time established',
  [CaseLogEventType.CaseLinkAdded]: 'Case link added',
  [CaseLogEventType.CaseLinkUpdated]: 'Case link updated',
  [CaseLogEventType.CaseLinkRemoved]: 'Case link removed',
  [CaseLogEventType.RequestOrganApproval]: 'Request organ approval',
  [CaseLogEventType.OrganApproved]: 'Organ Approved',
  [CaseLogEventType.OrganDeclined]: 'Organ Declined',
  [CaseLogEventType.CreateSurgeonCaseSummary]: 'Create Surgeon case summary',
  [CaseLogEventType.CaseInvoiceCreated]: 'Case invoice created',
  [CaseLogEventType.LightsSirensSupportChanged]: 'Lights and sirens support',
  [CaseLogEventType.RouteCommentUpdated]: 'Route comment updated',
  [CaseLogEventType.ServiceCompleted]: 'Service completed',
  [CaseLogEventType.CaseDataSyncStatusChanged]: 'Sync data from reference case',
  [CaseLogEventType.TransplantOutcomeTypeSet]: 'System update: missing case outcome data added.',
  [CaseLogEventType.CaseSalesOrderUpdated]: 'Sales Order status',
  [CaseLogEventType.CaseAssignedEmployeePrimaryChanged]: 'Primary designation updated ',
  [CaseLogEventType.CaseQuoteRecalled]: 'Quote recalled',
  [CaseLogEventType.RandomizationResultSet]: 'Randomization completed',
  [CaseLogEventType.QuestionnaireResultSet]: 'Questionnaire updated',
  [CaseLogEventType.IdentityAccessTicketGranted]: 'Access Ticket Granted',
  [CaseLogEventType.IdentityAccessTicketRevoked]: 'Access Ticket Revoked',
  [CaseLogEventType.ResourceTransferredToCase]: 'Clinical Resources transferred from the original case',
  [CaseLogEventType.ReAllocationClinicalDataCopied]: 'Clinical data copied from the original case',
  [CaseLogEventType.NotesCopied]: 'Notes copied from the original case',
  [CaseLogEventType.QuestionaryEditAllowanceChanged]: 'Questionary Edit Allowed updated',
  [CaseLogEventType.KidneyTrialChanged]: 'Case details updated',
  [CaseLogEventType.KidneyRandomizationResultSet]: 'Randomization completed',
  [CaseLogEventType.KidneyRandomizationResultRemoved]: 'Randomization removed',
  [CaseLogEventType.CaseResourceLabelsChanged]: 'Primary designation updated for case resource',
  [CaseLogEventType.DonorDataUpdated]: 'Unallocated clinical request updated',
  [CaseLogEventType.DonorDataReset]: 'Unallocated clinical request revoked',
  [CaseLogEventType.CaseSplitIntoOrgans]: 'Case split into organs',
  [CaseLogEventType.CaseInitiatedFromAnotherDraft]: 'Case initiated from another draft',
};

export const caseLogGroupNames: Record<CaseLogGroup, string> = {
  [CaseLogGroup.CaseFeedback]: 'Case feedback',
  [CaseLogGroup.CaseLogistics]: 'Case logistics',
  [CaseLogGroup.CaseManagement]: 'Case management',
  [CaseLogGroup.CaseUpdate]: 'Case update',
  [CaseLogGroup.FinancialManagement]: 'Financial management',
  [CaseLogGroup.ResourceManagement]: 'Resource management',
  [CaseLogGroup.AchieveCaseAccess]: 'Achieve Case Access',
  [CaseLogGroup.Unknown]: 'Unknown',
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  [undefined]: 'Unknown',
};

export const outcomeTypeNames: Record<OutcomeType, string> = {
  [OutcomeType.Aborted]: 'Aborted case',
  [OutcomeType.DryRun]: 'Dry run',
  [OutcomeType.Transplant]: 'Transplant',
  [OutcomeType.Turndown]: 'Turndown',
  [OutcomeType.DirectAcquisition]: 'Direct Acquisition',
};

export const dispositionReasonsNames: Record<DispositionReasonType, string> = {
  [DispositionReasonType.OrganNotSuitableForOCS]: 'Organ not suitable for OCS',
  [DispositionReasonType.CancelledByTransMedics]: 'Cancelled by TransMedics',
  [DispositionReasonType.ByTxCenter]: 'Cancelled by TX Center',
  [DispositionReasonType.WithdrawnByOPO]: 'Withdrawn by OPO',
  [DispositionReasonType.ErroneousCase]: 'Erroneous case',
  [DispositionReasonType.DCDDonorDidNotExpire]: 'Donor Did Not Expire',
  [DispositionReasonType.TurndownByTxCenter]: 'Turndown by TX Center',
  [DispositionReasonType.OCSContraindication]: 'OCS Contraindication',
  [DispositionReasonType.DeviceMalfunction]: 'Device Malfunction',
  [DispositionReasonType.DonationWithdrawnByOPO]: 'Donation Withdrawn by OPO',
  [DispositionReasonType.SurgicalError]: 'Surgical Error',
  [DispositionReasonType.Other]: 'Other',
};

export const dispositionSpecificationNames: Record<DispositionSpecificationType, string> = {
  [DispositionSpecificationType.None]: 'None',
  [DispositionSpecificationType.DonorReasons]: 'Donor Reasons',
  [DispositionSpecificationType.RecipientReasons]: 'Recipient Reasons',
  [DispositionSpecificationType.LogisticsCosts]: 'Logistics Costs',
  [DispositionSpecificationType.AcceptedAlternateDonor]: 'Accepted Alternate Donor',
  [DispositionSpecificationType.ChangedToAnAlternatePreservationMethod]: 'Changed To An Alternate Preservation Method',
  [DispositionSpecificationType.DonorQuality]: 'Donor Quality',
  [DispositionSpecificationType.OrganNotViable]: 'Organ Not Viable',
  [DispositionSpecificationType.LogisticalIssues]: 'Logistical Issues',
  [DispositionSpecificationType.TXCenterAcceptanceCriteria]: 'TX Center Acceptance Criteria',
  [DispositionSpecificationType.AlternatePreservationMethod]: 'Alternate Preservation Method',
  [DispositionSpecificationType.OCSContraindicationOrNotSuitable]: 'OCS Contraindication / Not Suitable',
  [DispositionSpecificationType.NoClinicalSpecialists]: 'No Clinical Specialists',
  [DispositionSpecificationType.NoSurgeon]: 'No Surgeon',
  [DispositionSpecificationType.NoDevice]: 'No Device',
  [DispositionSpecificationType.TooShortNotice]: 'Too Short Notice',
  [DispositionSpecificationType.NoGroundTransport]: 'No Ground Transport',
  [DispositionSpecificationType.NoPlanes]: 'No Aviation',
  [DispositionSpecificationType.Other]: 'Other',
};

export const caseSalesOrderStatusNames = {
  [CaseSalesOrderStatus.Error]: 'Rejected by QAD',
  [CaseSalesOrderStatus.Success]: 'Processed by QAD',
  [CaseSalesOrderStatus.Sent]: 'Sent to QAD',
  [CaseSalesOrderStatus.Reprocess]: 'Rejected by QAD and will be resent later',
};

export const dryRunReasonNames = {
  ...dispositionReasonsNames,
  [DispositionReasonType.TurndownByTxCenter]: 'Declined by TX Center',
};

export const UNOS_ID_REGEX = /^([a-z]{3}\d{4}|[a-z]{4}\d{3})$/i;
export const EUROPE_UNOS_ID_REGEX = /^[a-z0-9]{1,10}$/i;

export const liverQualityNames: Record<LiverQuality, string> = {
  [LiverQuality.NoQualityIssues]: 'No Quality Issues',
  [LiverQuality.Congested]: 'Congested',
  [LiverQuality.Steatotic]: 'Steatotic',
  [LiverQuality.Cirrhotic]: 'Cirrhotic',
  [LiverQuality.CalcifiedVessels]: 'Calcified Vessels',
  [LiverQuality.Other]: 'Other',
};

export const flushTypeNames: Record<FlushType, string> = {
  [FlushType.Good]: 'Good',
  [FlushType.DidNotFlushWell]: 'Did Not Flush Well',
};

export const liverOutcomeNames: Record<LiverOutcome, string> = {
  [LiverOutcome.PlacedOnOCSLiver]: 'Placed On OCS Liver - no issues',
  [LiverOutcome.NotPlacedOnOCSLiver]: 'Not Placed On OCS Liver - poor quality',
  [LiverOutcome.DCDNotPlacedOnOCSLiver]: 'DCD Not Placed On OCS Liver - expired beyond warm time limits',
};

export const aberrantAnatomyTypeNames: Record<AberrantAnatomyType, string> = {
  [AberrantAnatomyType.ReplacedRightOrAccessoryRightHepaticArtery]: 'Replaced Right Or Accessory Right Hepatic Artery',
  [AberrantAnatomyType.ReplacedLeftOrAccessoryLeftHepaticArtery]: 'Replaced Left Or Accessory Left Hepatic Artery',
  [AberrantAnatomyType.Other]: 'Other',
};

export const procurementEnjuryTypeNames: Record<ProcurementEnjuryType, string> = {
  [ProcurementEnjuryType.ArterialInjury]: 'Arterial Injury',
  [ProcurementEnjuryType.BiliaryInjury]: 'Biliary Injury',
  [ProcurementEnjuryType.PortalVeinInjury]: 'Portal Vein Injury',
  [ProcurementEnjuryType.ParenchymalInjury]: 'Parenchymal Injury',
  [ProcurementEnjuryType.Other]: 'Other',
};

export const backtableInjuryTypeNames: Record<BacktableInjuryType, string> = {
  [BacktableInjuryType.ArterialInjury]: 'Arterial Injury',
  [BacktableInjuryType.BiliaryInjury]: 'Biliary Injury',
  [BacktableInjuryType.VenousInjury]: 'Venous Injury',
};

export const noLiverInjuryTypeNames: Record<NoLiverInjuryType, string> = {
  [NoLiverInjuryType.IntestinalPerforationWithContamination]: 'Intestinal Perforation With Contamination',
  [NoLiverInjuryType.KidneyInjury]: 'Kidney Injury',
  [NoLiverInjuryType.PancreasInjury]: 'Pancreas Injury',
  [NoLiverInjuryType.Other]: 'Other',
};

export const impactOfInjuryTypeNames: Record<ImpactOfInjuryType, string> = {
  [ImpactOfInjuryType.NoImpact]: 'No Impact',
  [ImpactOfInjuryType.OrganUsedButInjuryChangedAllocationOrResultedInAlterationToPractice]:
    'Organ Used But Injury Changed Allocation Or Resulted In Alteration To Practice',
  [ImpactOfInjuryType.OrganDeclined]: 'Organ Declined',
};

export const assistanceTypeNames: Record<AssistanceType, string> = {
  [AssistanceType.TrainedProcurementSurgeon]: 'Trained Procurement Surgeon',
  [AssistanceType.TrainedSurgicalAssistant]: 'Trained Surgical Assistant',
  [AssistanceType.SurgicalResidentOrFellow]: 'Surgical Resident Or Fellow',
  [AssistanceType.UnspecializedSurgicalAssistant]: 'Unspecialized Surgical Assistant',
  [AssistanceType.None]: 'None',
};

export const reviewStatusNames: Record<ReviewStatus, string> = {
  [ReviewStatus.Approved]: 'Approved',
  [ReviewStatus.Rejected]: 'Rejected',
  [ReviewStatus.NotApplicable]: 'Not applicable',
  [ReviewStatus.WaitingForApproval]: 'Waiting for approval',
};

export const randomizationFilterTypeNames: Record<RandomizationFilterType, string> = {
  [RandomizationFilterType.OCS]: 'OCS',
  [RandomizationFilterType.SCS]: 'SCS',
  [RandomizationFilterType.NotRandomized]: 'No randomization',
};

export const caseQuoteRejectedReasonNames: Partial<Record<QuoteRejectReason, string>> = {
  [QuoteRejectReason.FullOwnLogistics]: 'Use TX Center full own logistics',
  [QuoteRejectReason.NotSuitable]: 'TMDX Quote is not suitable',
  [QuoteRejectReason.OwnAirLogistics]: 'Use TX Center air logistics',
  [QuoteRejectReason.OwnGroundLogistics]: 'Use TX Center ground logistics',
};

export const invoiceStateNames: Record<InvoiceState, string> = {
  [InvoiceState.NoInvoice]: 'No Invoice',
  [InvoiceState.InvoiceAdded]: 'Invoice Added',
};

export const invoiceStatusNames: Record<InvoiceStatus, string> = {
  [InvoiceStatus.Open]: 'Open',
  [InvoiceStatus.PartiallyCredited]: 'Partially Credited',
  [InvoiceStatus.FullyCredited]: 'Fully Credited',
  [InvoiceStatus.PartiallyPaid]: 'Partially Paid',
  [InvoiceStatus.Paid]: 'Paid',
};

export const roleByCaseUserGroup: Partial<Record<CaseUserGroup, EmployeeRole>> = {
  [CaseUserGroup.LogisticsManagers]: EmployeeRole.LogisticsManager,
  [CaseUserGroup.ClinicalCoordinators]: EmployeeRole.ClinicalCoordinator,
  [CaseUserGroup.ClinicalLeads]: EmployeeRole.ClinicalLead,
  [CaseUserGroup.AviationSpecialists]: EmployeeRole.AviationSpecialist,
  [CaseUserGroup.GroundSpecialists]: EmployeeRole.GroundSpecialist,
  [CaseUserGroup.SrLogisticsManagers]: EmployeeRole.SrLogisticsManager,
  [CaseUserGroup.QuoteApprovers]: EmployeeRole.QuoteApprovers,
  [CaseUserGroup.OrganLeadership]: EmployeeRole.OrganLeadership,
  [CaseUserGroup.OrganManagement]: EmployeeRole.OrganManagement,
  [CaseUserGroup.OrganManagementSpecialists]: EmployeeRole.OrganManagementSpecialist,
};

export const roleByCaseTxUserGroup: Partial<Record<CaseUserGroup, CustomerRoleT>> = {
  [CaseUserGroup.CustomerAdministrators]: CustomerRole.TxCenterAdministrator,
  [CaseUserGroup.CustomerCoordinators]: CustomerRole.TxCenterCoordinator,
  [CaseUserGroup.CustomerReceivingSurgeons]: CustomerRole.ReceivingSurgeon,
  [CaseUserGroup.CustomerTransplantSupporters]: CustomerRole.TxCenterTransplantSupport,
};

export const roleByCaseGroup: Partial<Record<CaseUserGroup, UserRole>> = {
  ...roleByCaseUserGroup,
  ...roleByCaseTxUserGroup,
};

export const caseGroupByRole = invert(roleByCaseGroup) as RoleKeyedPartial<CaseUserGroup>;

export const TX_CENTER_CONTACTS_ROLES_WEIGHT: RolesWeightType = {
  [CustomerRole.TxCenterAdministrator]: 1,
  [CustomerRole.TxCenterCoordinator]: 2,
  [CustomerRole.ReceivingSurgeon]: 3,
  [CustomerRole.TxCenterTransplantSupport]: 4,
  [CustomerRole.AdminAuditor]: 5,
};

export const DONOR_TYPE_TEMPLATE_WEIGHT: OrganAcceptanceCriteriaWeightType = {
  [OrganType.Heart]: 1,
  [OrganType.Liver]: 2,
  [OrganType.Kidney]: 3,
};

export const CLINICAL_CASE_MANAGEMENT_GROUP_WEIGHT: GroupWeightType = clinicalManagementGroups.reduce(
  (acc, group, index) => ({
    ...acc,
    [group]: index + 1,
  }),
  {},
);

export const LOGISTICS_CASE_MANAGEMENT_GROUP_WEIGHT: GroupWeightType = logisticsManagementGroups.reduce(
  (acc, group, index) => ({
    ...acc,
    [group]: index + 1,
  }),
  {},
);

export const CASE_MANAGEMENT_GROUP_WEIGHT: GroupWeightType = [
  ...clinicalManagementGroups,
  ...logisticsManagementGroups,
].reduce(
  (acc, group, index) => ({
    ...acc,
    [group]: index + 1,
  }),
  {},
);

export const trialAnswerNames: Record<TrialAnswer, string> = {
  [TrialAnswer.Yes]: 'Yes',
  [TrialAnswer.No]: 'No',
  [TrialAnswer.LUNGGuard]: 'LUNGGuard',
  [TrialAnswer.BAROGuard]: 'BAROGuard',
  [TrialAnswer.StandardColdPreservation]: 'Standard Cold Preservation',
  [TrialAnswer.Other]: 'Other',
  [TrialAnswer.PartA]: 'Part A (single arm)',
  [TrialAnswer.PartB]: 'Part B (randomized)',
  [TrialAnswer.Paragonix]: 'Paragonix',
  [TrialAnswer.SherpaPak]: 'SherpaPak',
  [TrialAnswer.IceCooler]: 'Ice Cooler',
};

export const randomizationTypeNames: Record<RandomizationResultType, string> = {
  [RandomizationResultType.SCS]: 'Static Cold Storage',
  [RandomizationResultType.OCS]: 'Organ Care System (OCS)',
  [RandomizationResultType.NoRandomization]: 'No randomization',
};

export const trialImageByOrgan = {
  [OrganType.Heart]: EnhanceLogoUrl,
  [OrganType.Lungs]: DenovoLogoUrl,
  [OrganType.Liver]: '',
  [OrganType.Kidney]: KidneyTrialLogoUrl,
};

export const trialProgramByOrgan = {
  [OrganType.Heart]: 'ENHANCE',
  [OrganType.Lungs]: 'DENOVO',
  [OrganType.Liver]: '',
  [OrganType.Kidney]: '',
};

export const caseFinishingStatuses = [CaseStatus.Finished, CaseStatus.Closed];

const customCustomerCaseStatusNames: Record<CustomCustomerCaseStatus, string> = {
  [CustomCustomerCaseStatus.OrganOnSCS]: 'Organ on SCS',
  [CustomCustomerCaseStatus.OrganOffSCS]: 'Organ SCS Handoff',
};

export const customerCaseStatusNames: Record<CustomerCaseStatus | CustomCustomerCaseStatus, string> = {
  [CustomerCaseStatus.Undefined]: 'Status',
  [CustomerCaseStatus.Draft]: 'Draft',
  [CustomerCaseStatus.UnderClinicalScreening]: 'Under Clinical Screening',
  [CustomerCaseStatus.CaseInitiatedForOCS]: 'Case Initiated for OCS',
  [CustomerCaseStatus.CaseDeclined]: 'Case declined',
  [CustomerCaseStatus.LogisticsQuoteReceived]: 'Logistics Quote Received',
  [CustomerCaseStatus.LogisticsQuoteRecalled]: 'Logistics Quote Recalled',
  [CustomerCaseStatus.LogisticsQuoteDeclined]: 'Logistics Quote Declined',
  [CustomerCaseStatus.TeamDeploymentInProgress]: 'Team Deployment in Progress',
  [CustomerCaseStatus.AtDonorHospital]: 'At Donor Hospital',
  [CustomerCaseStatus.CaseCanceled]: 'Case Canceled',
  [CustomerCaseStatus.TransplantNotCompleted]: 'Transplant not Completed',
  [CustomerCaseStatus.OrganOnOCS]: 'Organ on OCS',
  [CustomerCaseStatus.OrganInTransit]: 'Organ in Transit',
  [CustomerCaseStatus.OrganAtTXCenter]: 'Organ at TX Center',
  [CustomerCaseStatus.OrganOffOCS]: 'Organ off OCS',
  [CustomerCaseStatus.NOPServiceCompleted]: 'NOP Service Completed',
  [CustomerCaseStatus.NOPInvoiceReceived]: 'NOP Invoice Received',
  [CustomerCaseStatus.CaseClosed]: 'Case Closed',
  [CustomerCaseStatus.PendingServiceApproval]: 'Pending Service Approval',
  [CustomerCaseStatus.ServiceRejectedByTxc]: 'Case rejected',
  ...customCustomerCaseStatusNames,
};
